<template>
    <QuotationFeedback
        :modal="quotationFeedbackModal"
        :toast="quotationFeedbackToast"
        @close-modal="CloseQuotationFeedbackModal"
        @close-toast="CloseQuotationFeedbackToast"
    />
     <BreadcrumbsProject
        v-if="!isDeluxeLayout"
        :breadcrumbs="[
            {
                link : dotnetUrl +'/project/list',
                title : $t('Projects'),
                isRouterLink: false
            },
            {
                link : projectUrl +'/Project/' + projectInfo._id,
                title :  (projectInfo.projectNumber || '-') +' ' + (projectInfo.projectName || ''),
                isRouterLink: false
            },
            {
                link : '/boq/' + projectInfo._id+'/quotation/list',
                title : $t('Quotations'),
                isRouterLink: true
            },
        ]"
    ></BreadcrumbsProject>
    <div>
        <StepItem2 :active-index="isDeluxeLayout ? 2 : null" :deluxe="isDeluxeLayout"></StepItem2>
    </div>
    <div
        v-if="isDeluxeLayout"
        class="deluxe-project-back"
    >
        <a :href="projectUrl + '/project/' + projectInfo._id">
            <i class="fa-solid fa-chevron-left"></i> {{ $t('Back to Project Dashboard') }}
        </a>
    </div>
    <div
        v-if="isDeluxeLayout"
        class="deluxe-page-header"
    >
        <div class="deluxe-page-heading">
            <h1 class="deluxe-page-title">{{ $t('Quotation') }}</h1>
            <p><i>{{ $t('All monetary figures shown below are in') }} <strong>{{ currencyLabel }}</strong> {{ $t('and are exclusive of tax.') }}</i></p>
        </div>
        <form class="deluxe-page-tools" @submit.prevent>
            <div class="deluxe-transaction-search">
                <input
                    type="search"
                    v-model.trim="pageParams.search"
                    :placeholder="$t('Search Transaction')"
                    @keydown.enter.prevent
                    @search="QueueQuotationSearch"
                />
                <button type="button" tabindex="-1" aria-hidden="true">
                    <i class="fa-solid fa-magnifying-glass"></i>
                </button>
            </div>
            <select class="deluxe-status-select" v-model="statusFilter">
                <option value="">{{ $t('Any Status') }}</option>
                <option
                    v-for="(stage, index) in ApprovalStageBoqObj"
                    :key="index"
                    :value="String(index + 1)"
                >
                    {{ stage.name }}
                </option>
            </select>
            <button type="button" class="deluxe-filter-button" :title="$t('Filter')" @click="SearchQuotations">
                <i class="fa-solid fa-filter"></i>
            </button>
            <button
                v-if="permission.indexOf(ModuleActionList.BOQ_QUOTATION_CREATE)>-1"
                type="button"
                class="custom-btn"
                data-bs-target="#BoqModalAddQuotation"
                data-bs-toggle="modal"
                @click="OpenModalAdd"
            >
                <span>{{ $t('Add Quotation') }}</span>
                <i class="fa-solid fa-plus"></i>
            </button>
        </form>
    </div>
    <div :class="{ 'border-top': !isDeluxeLayout }">
        <div class="card">
            <div class="card-header bg-white" v-if="!isDeluxeLayout && permission.indexOf(ModuleActionList.BOQ_QUOTATION_CREATE)>-1">
                <div class="d-flex justify-content-end">
                    <button class="btn btn-primary"
                    data-bs-target="#BoqModalAddQuotation" data-bs-toggle="modal" @click="OpenModalAdd"
                    ><i class="fa-solid fa-plus"></i> {{$t('Add Quotation')}}</button>
                </div>
            </div>
            <div class="card-body">
                <div class="table-responsive">
                    <table :class="isDeluxeLayout ? 'custom-table fixed-width quotation-list-table' : 'table table-striped'">
                        <thead>
                            <tr>
                                <th>{{ isDeluxeLayout ? $t('Qtn Number') : $t('Quotation No') }}</th>
                                <th class="description">{{ $t('Description') }}</th>
                                <th class="amount">{{ $t('Amount') }}</th>
                                <th class="status">{{ $t('Status') }}</th>
                                <th class="actions-heading">{{ isDeluxeLayout ? $t('Actions') : '' }}</th>
                            </tr>
                        </thead>
                        <tbody v-if="loadError">
                            <tr>
                                <td colspan="5">
                                    <div class="deluxe-empty-state">
                                        <strong>{{ loadError }}</strong>
                                        <span>{{ $t('Please check API connection or CORS settings') }}</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else-if="isLoading">
                            <tr>
                                <td colspan="5">
                                    <Loader />
                                </td>
                            </tr>
                        </tbody>
                        <tbody v-else>
                            <tr v-if="filteredQuotationDocs.length==0">
                                <td class="text-center" colspan="5">
                                    {{ $t('No data Found') }}
                                </td>
                            </tr>
                            <tr v-for="(item, key) in filteredQuotationDocs" :key="key">
                                <td>
                                    <router-link :to="{name : 'BoqQuotation', params : {id : item._id} }" class="link-item-route text-capitalize"> 
                                        {{item.quotationNumber}} {{item.quotationName}}
                                    </router-link>
                                </td>
                                <td class="description">
                                <small>
                                        {{ item.remark || '-' }}
                                    </small>
                                </td>
                                <td :class="['amount', { 'amount-negative': IsNegativeAmount(item.amount) }]">
                                    {{ FormatQuotationAmount(item.amount) }}
                                </td>
                                <td class="status">
                                    <span :class="GetStatusClass(item.approvalStage)">{{ GetStatusLabel(item.approvalStage) }}</span>
                                </td>
                                <td :class="{ actions: isDeluxeLayout }">
                                    <template v-if="isDeluxeLayout">
                                        <router-link
                                            v-if="CanEditQuotation(item)"
                                            :to="{name : 'BoqQuotation', params : {id : item._id}, query : {isEdit : true} }"
                                            :title="$t('Edit Quotation')"
                                        >
                                            <i class="fa-solid fa-pencil"></i>
                                        </router-link>
                                        <router-link :to="{name : 'BoqQuotation', params : {id : item._id} }" :title="$t('View Quotation')">
                                            <i class="fa-regular fa-eye"></i>
                                        </router-link>
                                        <button @click="DeleteQuotation(item._id,item.quotationName,item.quotationNumber)" :title="$t('Delete Quotation')">
                                            <i class="fa-solid fa-xmark"></i>
                                        </button>
                                    </template>
                                    <template v-else>
                                        <router-link :to="{name : 'BoqQuotation', params : {id : item._id} }" :class="GetActionClass('primary')"> 
                                            <i class="fa-solid fa-magnifying-glass"></i>
                                        </router-link>
                                        <router-link v-if="item.approvalStage!=1"
                                        :to="{name : 'BoqQuotationHistory', params : {id : item._id} }" :class="GetActionClass('primary')"> 
                                            <i class="fa-solid fa-clock-rotate-left"></i>
                                        </router-link>
                                        <button :class="GetActionClass('danger')" @click="DeleteQuotation(item._id,item.quotationName,item.quotationNumber)" :title="$t('Delete Quotation')">
                                            <i class="fa-solid fa-trash-can"></i>
                                        </button>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>   
                </div>
            </div>
        </div>
    </div>
    <Modal idModal="BoqModalAddQuotation" :headerTitle="$t('Add Quotation')" modalSize="modal-md" v-if="permission.indexOf(ModuleActionList.BOQ_QUOTATION_CREATE)>-1">
        <div v-if="isDeluxeLayout" class="quotation-create-modal">
            <div class="quotation-create-field">
                <label>{{ $t('Project') }}</label>
                <input class="quotation-create-input quotation-create-input--readonly" :value="quotationProjectDisplay" disabled />
            </div>
            <div class="quotation-create-group">
                <div class="quotation-create-field">
                    <label>{{ $t('Quotation No.') }} <span>({{ $t('Required') }})</span></label>
                    <input class="quotation-create-input" :placeholder="$t('Enter Quotation No.')" v-model="newQuotation.qtnNumber" />
                    <small class="quotation-create-validation" v-if="saveClicked && !newQuotation.qtnNumber">{{$t('Required')}}</small>
                </div>
                <div class="quotation-create-field">
                    <label>{{ $t('Name') }} <span>({{ $t('Required') }})</span></label>
                    <input class="quotation-create-input" :placeholder="$t('Enter Name')" v-model="newQuotation.qtnName" />
                    <small class="quotation-create-validation" v-if="saveClicked && !newQuotation.qtnName">{{$t('Required')}}</small>
                </div>
            </div>
        </div>
        <template v-else>
            <div class="form-group">
                <label class="required-style"> {{$t('Quotation Module.Quotation Number')}} </label>
                <input class="form-control" :placeholder="$t('Quotation Module.Quotation Number')" v-model="newQuotation.qtnNumber" />
                <small class="text-warning" v-if="saveClicked && !newQuotation.qtnNumber">{{$t('Required')}}</small>
            </div>
            <div class="form-group">
                <label class="required-style">  {{ $t('Name') }} </label>
                <input class="form-control" :placeholder="$t('Name')" v-model="newQuotation.qtnName" />
                <small class="text-warning" v-if="saveClicked && !newQuotation.qtnName">{{$t('Required')}}</small>
            </div>
        </template>
        <template v-slot:actions>
            <button type="button" ref="closeBoqModalAddQuotation" :class="isDeluxeLayout ? 'quotation-create-button quotation-create-button--cancel' : 'btn btn-outline-danger'" data-bs-dismiss="modal"><span v-if="!isDeluxeLayout">X </span>{{$t('Cancel')}}</button>
            <button type="button" :class="isDeluxeLayout ? 'quotation-create-button quotation-create-button--primary' : 'btn btn-primary'" @click="CreateQuotation" ><i v-if="!isDeluxeLayout" class="fas fa-save"></i> {{ isDeluxeLayout ? $t('Save and Continue Editing') : $t('Save Changes') }}</button>
        </template>
    </Modal>
    
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import {GetQuotationList, AddQuotation,DeleteQuotation} from '@/actions/boqActions.js'
import { LinkList, ReformatNumber, ApprovalStageBoq, ApprovalStageBoqObj,ModuleActionList } from '@/helpers/utilities.js'
import StepItem2 from '@/components/layout/StepItem2.vue'
import Loader from '@/components/layout/Loader.vue'
import Modal from '@/components/layout/Modal.vue'
import BreadcrumbsProject  from '@/components/projectInformation/BreadcrumbsProject.vue'
import QuotationFeedback from '@/components/boq/QuotationFeedback.vue'
import 'dotenv/config'
export default {
    name : 'BoqListQuotation',
    components : {
        StepItem2, BreadcrumbsProject, Loader, Modal, QuotationFeedback
    },
    mounted : async function(){
        const self = this
        const projectId = self.$route.params.id
        self.ChangeActiveLink(LinkList.QUOTATIONS)
        const project = await self.GetProjectInfo(projectId).catch(()=>null)
        if(!project){
            self.loadError = self.$t('Unable to load project information')
            self.isLoading = false
            return
        }

        await self.LoadQuotations()
    },
    methods : {
        ...mapActions(['ChangeActiveLink', 'GetProjectInfo']),
        async LoadQuotations(){
            this.isLoading = true
            this.loadError = ''

            const list = await GetQuotationList(this.pageParams).catch(()=>null)
            if(list=='401'){
                return this.$router.push('/unauthorized')
            }
            if(!list || !list.docs){
                this.loadError = this.$t('Unable to load quotations')
                this.quotationList = {
                    docs: [],
                    totalPages : 0,
                    page : 1
                }
                this.isLoading = false
                return
            }
            this.quotationList = list 
            this.isLoading = false
        },
        SearchQuotations(){
            if(this.searchTimer){
                clearTimeout(this.searchTimer)
                this.searchTimer = null
            }

            this.activeSearchText = this.pageParams.search || ''
            this.activeStatusFilter = this.statusFilter || ''
            this.pageParams.page = 1
            this.LoadQuotations()
        },
        QueueQuotationSearch(){
            if(!this.isDeluxeLayout){
                return
            }

            if(this.searchTimer){
                clearTimeout(this.searchTimer)
                this.searchTimer = null
            }

            this.searchTimer = setTimeout(()=>{
                this.SearchQuotations()
            }, 500)
        },
        ReformatNumber(value){
            return ReformatNumber(value)
        },
        FormatCurrencyName(value){
            return String(value || '')
                .replace(/\s+/g, ' ')
                .trim()
                .replace(/[A-Za-z]+/g, (word)=>{
                    if(word.length <= 3 && word === word.toUpperCase()){
                        return word
                    }

                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                })
        },
        GetNumericAmount(value){
            if(typeof value === 'number'){
                return value
            }

            const rawValue = String(value || '').trim()
            const isParenthesized = /^\(.*\)$/.test(rawValue)
            const parsedValue = Number(rawValue.replace(/[^\d.-]/g, ''))

            if(Number.isNaN(parsedValue)){
                return 0
            }

            return isParenthesized ? -Math.abs(parsedValue) : parsedValue
        },
        IsNegativeAmount(value){
            return this.GetNumericAmount(value) < 0
        },
        FormatQuotationAmount(value){
            const amount = this.GetNumericAmount(value)
            const currencySign = this.projectInfo.currencyId?.sign || ''
            const formattedValue = `${currencySign}${ReformatNumber(Math.abs(amount))}`

            return amount < 0 ? `( ${formattedValue} )` : formattedValue
        },
        GetActionClass(type){
            if(this.isDeluxeLayout){
                return ''
            }

            return type == 'danger' ? 'btn btn-outline-danger btn-action' : 'btn btn-outline-primary btn-action me-1'
        },
        GetStatusClass(stage){
            if(!this.isDeluxeLayout){
                return 'badge ' + ApprovalStageBoqObj[stage -1].color
            }

            if(stage==1){
                return 'quotation-status-pill quotation-status-pill--draft'
            }

            if(stage==2){
                return 'quotation-status-pill quotation-status-pill--pending'
            }

            if([5,8].indexOf(stage)>-1){
                return 'quotation-status-pill quotation-status-pill--approved'
            }

            if(stage==4){
                return 'quotation-status-pill quotation-status-pill--approval'
            }

            if(stage==3){
                return 'quotation-status-pill quotation-status-pill--reviewed'
            }

            if(stage==6){
                return 'quotation-status-pill quotation-status-pill--revision'
            }

            return 'quotation-status-pill quotation-status-pill--neutral'
        },
        GetStatusLabel(stage){
            const name = ApprovalStageBoqObj[stage - 1]?.name || '-'
            return name
                .toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase())
        },
        CanEditQuotation(item){
            if(this.permission.indexOf(ModuleActionList.BOQ_QUOTATION_EDIT)<0){
                return false
            }

            return [
                ApprovalStageBoq.FOR_APPROVAL,
                ApprovalStageBoq.APPROVED,
                ApprovalStageBoq.PUBLISHED
            ].indexOf(item.approvalStage)<0
        },
        OpenModalAdd(){
            this.newQuotation = {
                qtnName : '',
                qtnNumber : '',
                projectId : this.$route.params.id
            }
            this.saveClicked = false
        },
        ShowQuotationFeedbackModal(type, title, message){
            this.quotationFeedbackModal = {
                visible : true,
                type,
                title,
                message
            }
        },
        CloseQuotationFeedbackModal(){
            this.quotationFeedbackModal = {
                ...this.quotationFeedbackModal,
                visible : false
            }
        },
        ShowQuotationFeedbackToast(type, title, message){
            this.quotationFeedbackToast = {
                visible : true,
                type,
                title,
                message
            }
        },
        CloseQuotationFeedbackToast(){
            this.quotationFeedbackToast = {
                ...this.quotationFeedbackToast,
                visible : false
            }
        },
        async CreateQuotation(e){
            const btn = e && (e.currentTarget || e.target)
            const self = this
            self.saveClicked = true

            if(self.isAdding){
                return
            }
            const data = self.newQuotation
            data.projectId = this.$route.params.id

            if(!data.qtnName || !data.qtnNumber){
                return
            }
            self.saveClicked = false
            if(btn){
                btn.disabled = true
            }
            self.isAdding = true

            if(self.isDeluxeLayout){
                self.$refs.closeBoqModalAddQuotation.click()
                self.ShowQuotationFeedbackModal(
                    'loading',
                    self.$t('Quotation Module.Preparing your Quotation'),
                    self.$t('Quotation Module.Setting up your Quotation and analyzing your table sheet')
                )
            }

            try{
                const res = await AddQuotation(data)
                if(res.status!=200){
                    self.isAdding = false
                    if(btn){
                        btn.disabled = false
                    }
                    if(self.isDeluxeLayout){
                        self.ShowQuotationFeedbackModal(
                            'error',
                            self.$t('Quotation Module.Sorry, there was a problem'),
                            self.$t('Quotation Module.Please check your internet connection. If the problem persists, please try again at a later time')
                        )
                    }else{
                        self.$swal(self.$i18n.t('Add Quotation'),res,'warning')
                    }
                    return
                }

                self.isAdding = false
                if(self.isDeluxeLayout){
                    self.ShowQuotationFeedbackModal(
                        'success',
                        self.$t('Success!'),
                        self.$t('Quotation Module.You have successfully created a new Quotation') + '<br />' + self.$t('Quotation Module.This page will redirect in a few seconds')
                    )
                    setTimeout(()=>{
                        self.$router.push({name : 'BoqQuotation', params :{id : res.data}, query : {isEdit : true}})
                    }, 1200)
                }else{
                    self.$refs.closeBoqModalAddQuotation.click()
                    self.$router.push({name : 'BoqQuotation', params :{id : res.data}, query : {isEdit : true}})
                }
                // GetQuotationList(self.pageParams).then(data=>{
                //     self.quotationList = data
                // })
            }catch(error){
                self.isAdding = false
                if(btn){
                    btn.disabled = false
                }
                if(self.isDeluxeLayout){
                    self.ShowQuotationFeedbackModal(
                        'error',
                        self.$t('Quotation Module.Sorry, there was a problem'),
                        self.$t('Quotation Module.Please check your internet connection. If the problem persists, please try again at a later time')
                    )
                }else{
                    self.$swal(self.$i18n.t('Add Quotation'), error.message || error, 'warning')
                }
            }
        },
        DeleteQuotation(id,name, number){
            const self = this

            const swalTitle = self.$i18n.t('Add Quotation')

            self.$swal({
                title: swalTitle,
                text : `"${number} ${name} ${self.$i18n.t('will be Deleted Permanently, Proceed?')}`,
                showCancelButton: true,
                icon: 'warning',
                confirmButtonText: 'Delete',
                showLoaderOnConfirm : true,
                allowOutsideClick: () => !self.$swal.isLoading(),
                preConfirm : ()=>{
                    return DeleteQuotation(id).then((res)=>{
                        return res
                    }).catch(()=> 'ERROR')
                }
            }).then((res)=>{
                if(res.isConfirmed){
                    if(res.value=='OK'){
                        const docs = self.quotationList.docs
                        const getIndex = docs.findIndex(u=>u._id==id)
                        if(getIndex!=-1){
                            docs.splice(getIndex,1)
                        }
                        if(self.isDeluxeLayout){
                            self.ShowQuotationFeedbackToast(
                                'success',
                                self.$t('Success'),
                                self.$t('Quotation Module.You have successfully deleted an entry')
                            )
                        }else{
                            self.$swal(swalTitle,self.$i18n.t('Deleted Successfully'),'success')
                        }
                    }else{
                        if(self.isDeluxeLayout){
                            self.ShowQuotationFeedbackToast(
                                'error',
                                self.$t('Failed'),
                                self.$t('Quotation Module.An error occured while deleting this entry. Try again later')
                            )
                        }else{
                            self.$swal(swalTitle,res.value,'warning')
                        }
                    }
                
                }
            })
        }
    },
    watch : {
        'pageParams.search'(){
            this.QueueQuotationSearch()
        }
    },
    beforeUnmount(){
        if(this.searchTimer){
            clearTimeout(this.searchTimer)
        }
    },
    computed : {
        ...mapGetters(['projectInfo','permission']),
        isDeluxeLayout(){
            return this.$route.meta.layout == 'deluxe'
        },
        quotationProjectDisplay(){
            return this.projectInfo.projectNumber || this.projectInfo.projectOldId || this.$route.params.id || '-'
        },
        currencyLabel(){
            const currency = this.projectInfo.currencyId || {}
            const currencyName = this.FormatCurrencyName(currency.name || currency.code || currency.shortName || currency.sign || '')
            const currencySign = currency.sign || ''

            if(currencyName && currencySign){
                return `${currencyName} (${currencySign})`
            }

            return currencyName || currencySign || '-'
        },
        filteredQuotationDocs(){
            let docs = this.quotationList.docs || []
            if(this.activeStatusFilter){
                docs = docs.filter((item)=>String(item.approvalStage) === this.activeStatusFilter)
            }

            const searchText = (this.activeSearchText || '').toLowerCase()
            if(!searchText){
                return docs
            }

            return docs.filter((item)=>{
                const status = ApprovalStageBoqObj[item.approvalStage - 1]?.name || ''
                const values = [
                    item.quotationNumber,
                    item.quotationName,
                    item.remark,
                    item.amount,
                    status
                ]

                return values.some((value)=>String(value || '').toLowerCase().indexOf(searchText)>-1)
            })
        }
    },
    data(){
        return {
            projectUrl : process.env.VUE_APP_PROJECT_URL,
            dotnetUrl : process.env.VUE_APP_DOTNET_URL,
            quotationList : {
                docs: [],
                totalPages : 0,
                page : 1
            },
            pageParams : {
                id : this.$route.params.id,
                search : '',
                page : 1,
            },
            newQuotation : {
                qtnName : '',
                qtnNumber : '',
                projectId  : this.$route.params.id
            },
            isLoading : true,
            loadError : '',
            statusFilter : '',
            activeStatusFilter : '',
            activeSearchText : '',
            searchTimer : null,
            saveClicked : false,
            isAdding : false,
            quotationFeedbackModal : {
                visible : false,
                type : 'loading',
                title : '',
                message : ''
            },
            quotationFeedbackToast : {
                visible : false,
                type : 'success',
                title : '',
                message : ''
            },
            ApprovalStageBoqObj : ApprovalStageBoqObj,
            ModuleActionList : ModuleActionList
        }
    }
}
</script>

<style scoped>
:deep(#BoqModalAddQuotation .modal-dialog) {
    max-width: 487px;
}

:deep(#BoqModalAddQuotation .modal-content) {
    border: 0;
    border-radius: 4px;
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.24);
}

:deep(#BoqModalAddQuotation .modal-header) {
    justify-content: center;
    border-bottom: 0;
    padding: 35px 22px 20px;
    position: relative;
}

:deep(#BoqModalAddQuotation .modal-title) {
    color: var(--lens-theme-color, #a9005c);
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    margin: 0;
    text-align: center;
}

:deep(#BoqModalAddQuotation .btn-close) {
    position: absolute;
    top: 23px;
    right: 20px;
    width: 18px;
    height: 18px;
    padding: 0;
    background-size: 14px;
    opacity: 0.65;
}

:deep(#BoqModalAddQuotation .modal-body) {
    padding: 0 23px 20px;
}

:deep(#BoqModalAddQuotation .modal-footer) {
    border-top: 0;
    gap: 20px;
    flex-wrap: nowrap;
    padding: 36px 23px 36px;
}

:deep(#BoqModalAddQuotation .modal-footer > *) {
    margin: 0;
}

.quotation-create-modal {
    color: #222;
}

.quotation-create-field {
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.quotation-create-field + .quotation-create-field {
    margin-top: 14px;
}

.quotation-create-field label {
    color: #333;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
}

.quotation-create-field label span {
    color: #e00000;
}

.quotation-create-input {
    width: 100%;
    min-height: 44px;
    border: 1px solid #d6d6d6;
    border-radius: 0;
    background: #fff;
    color: #333;
    font-size: 14px;
    line-height: 1.4;
    padding: 10px 21px;
}

.quotation-create-input::placeholder {
    color: #777;
    opacity: 1;
}

.quotation-create-input:focus {
    border-color: var(--lens-theme-color, #a9005c);
    box-shadow: none;
    outline: none;
}

.quotation-create-input--readonly {
    background: #efefef;
    color: #888;
    cursor: not-allowed;
}

.quotation-create-group {
    border: 1px solid #d6d6d6;
    border-radius: 4px;
    margin-top: 12px;
    padding: 21px 15px 25px;
}

.quotation-create-validation {
    color: #e00000;
    font-size: 12px;
    line-height: 1.2;
}

.quotation-create-button {
    align-items: center;
    border: 0;
    border-radius: 0;
    display: inline-flex;
    font-size: 15px;
    font-weight: 700;
    height: 43px;
    justify-content: center;
    line-height: 1;
    padding: 0 20px;
}

.quotation-create-button--cancel {
    background: #e3e3e3;
    color: #666;
    flex: 0 0 140px;
}

.quotation-create-button--primary {
    background: var(--lens-theme-color, #a9005c);
    color: #fff;
    flex: 1 1 0;
    min-width: 0;
}

.quotation-create-button--cancel:hover,
.quotation-create-button--cancel:focus {
    background: #d8d8d8;
    color: #555;
}

.quotation-create-button--primary:hover,
.quotation-create-button--primary:focus {
    background: var(--lens-theme-color, #a9005c);
    color: #fff;
    filter: brightness(0.95);
}

@media (max-width: 575.98px) {
    :deep(#BoqModalAddQuotation .modal-dialog) {
        max-width: calc(100% - 28px);
        margin-left: auto;
        margin-right: auto;
    }

    :deep(#BoqModalAddQuotation .modal-footer) {
        flex-direction: column-reverse;
        gap: 12px;
    }

    .quotation-create-button--cancel,
    .quotation-create-button--primary {
        flex: 0 0 auto;
        min-width: 0;
        width: 100%;
    }
}
</style>
